// src/views/customer/booking/customer_booking.jsx
import React, { useState, useEffect, useRef, useCallback } from "react"
import Calendar from "react-calendar"
import "react-calendar/dist/Calendar.css"

import { usersAPI, servicesAPI, workScheduleAPI, appointmentsAPI, api } from "services/api.js"
import { useAuth } from "context/AuthContext"

import {
    formatDate,
    getInitials,
    timeToMinutes,
    transformWorkDaysToSchedule,
    DAY_NAMES,
} from "./components/BookingConstants"

// ─── INLINE ICONS ────────────────────────────────────────────────────────────
const ScissorsIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
        <line x1="20" y1="4" x2="8.12" y2="15.88" />
        <line x1="14.47" y1="14.48" x2="20" y2="20" />
        <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
)
const StarIcon = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
)
const CheckIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
)
const ClockIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
)
const CalendarIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
)
const AlertIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
    </svg>
)
const XIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
)

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    /* ── CSS VARIABLES ── */
    .booking-page-wrapper {
      font-family: 'Plus Jakarta Sans', sans-serif;
      
      /* DİNAMİK VURGU (BRAND) RENKLERİ */
      --bk-accent: rgb(var(--color-brand-500));
      --bk-accent-light: rgb(var(--color-brand-500) / 0.12);
      --bk-accent-hover: rgb(var(--color-brand-600));
      
      --bk-success: #12b886;
      --bk-error: #fa5252;
      --bk-warning: #fd7e14;

      /* DİNAMİK AYDINLIK MOD (LIGHT MODE) */
     --bk-bg: transparent;
      --bk-card: #ffffff;
      --bk-card2: rgb(var(--color-brand-50) / 0.4); /* Seçili temanın tonunda soft alt kart */
      --bk-border: rgb(var(--color-brand-500) / 0.12);
      
      --bk-text-primary: rgb(var(--color-navy-900));
      --bk-text-secondary: rgb(var(--color-navy-500));
      --bk-text-muted: rgb(var(--color-navy-400));
      
      --bk-shadow: 0 4px 24px rgb(var(--color-brand-500) / 0.08);
      --bk-shadow-lg: 0 12px 40px rgb(var(--color-brand-500) / 0.14);
      
      --skeleton-base: rgb(var(--color-navy-200) / 0.5);
      --skeleton-shine: rgb(var(--color-navy-100) / 0.8);
      
      --cal-bg: #ffffff;
      --cal-text: rgb(var(--color-navy-900));
      --cal-tile-hover: rgb(var(--color-brand-500) / 0.08);
      --cal-nav-btn: rgb(var(--color-navy-500));
    }

    /* ── DARK MODE ── */
  .dark .booking-page-wrapper,
    [data-coreui-theme="dark"] .booking-page-wrapper,
    [data-bs-theme="dark"] .booking-page-wrapper {
      
      /* DİNAMİK KARANLIK MOD - Siyahlık Çözüldü! */
        --bk-bg: transparent;
      --bk-card: rgb(var(--color-navy-800)); /* Temanın soft kart rengi (Nordic, Espresso vs. buraya oturur) */
      --bk-card2: rgb(var(--color-navy-700) / 0.5); /* Kartların içindeki alt kutular için daha soft bir ton */
      --bk-border: rgb(var(--color-brand-500) / 0.25); /* Borderlar artık temanın vurgu rengine uyumlu */
      
      --bk-text-primary: #ffffff;
      --bk-text-secondary: rgb(var(--color-navy-200));
      --bk-text-muted: rgb(var(--color-navy-300));
      
      --bk-shadow: 0 4px 24px rgba(0,0,0,0.35);
      --bk-shadow-lg: 0 12px 40px rgba(0,0,0,0.5);
      
      --skeleton-base: rgb(var(--color-navy-700) / 0.5);
      --skeleton-shine: rgb(var(--color-navy-600) / 0.5);
      
      --cal-bg: rgb(var(--color-navy-800));
      --cal-text: #ffffff;
      --cal-tile-hover: rgb(var(--color-brand-500) / 0.15);
      --cal-nav-btn: rgb(var(--color-navy-300));
    }

    /* ── BASE ── */
   .booking-page-wrapper {
      background: var(--bk-bg);
      min-height: 100vh;
      padding: 16px;
    }
    @media (min-width: 768px) {
      .booking-page-wrapper { padding: 24px; }
    }

    /* ── ANIMATIONS ── */
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes successPop {
      0% { transform: scale(0); opacity: 0; }
      60% { transform: scale(1.2); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes ripple {
      0% { transform: scale(0.8); opacity: 0.6; }
      100% { transform: scale(2.5); opacity: 0; }
    }
    .slide-up { animation: slideUp 0.35s ease both; }

    /* ── HEADER CARD ── */
    .booking-header-card {
      background: var(--bk-card);
      border-radius: 20px;
      padding: 20px 24px;
      margin-bottom: 16px;
      border: 1px solid var(--bk-border);
      box-shadow: var(--bk-shadow);
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .booking-header-icon {
      width: 52px; height: 52px;
      background: var(--bk-accent-light);
      border-radius: 14px;
      display: flex; align-items: center; justify-content: center;
      color: var(--bk-accent);
      flex-shrink: 0;
    }
    .booking-header-title {
      font-size: 1.25rem; font-weight: 800;
      color: var(--bk-text-primary); margin: 0; line-height: 1.2;
    }
    .booking-header-sub {
      font-size: 0.8rem; color: var(--bk-text-muted);
      margin: 2px 0 0; font-weight: 500;
    }

    /* ── MAIN CARD ── */
    .booking-main-card {
      background: var(--bk-card);
      border-radius: 24px;
      border: 1px solid var(--bk-border);
      box-shadow: var(--bk-shadow);
      overflow: hidden;
      padding: 0;
    }
    .booking-main-body {
      padding: 20px 16px;
    }
    @media (min-width: 768px) {
      .booking-main-body { padding: 28px 28px; }
    }

    /* ── ALERT ── */
    .booking-alert {
      background: rgba(250,82,82,0.1);
      border: 1px solid rgba(250,82,82,0.25);
      border-radius: 12px;
      padding: 12px 16px;
      display: flex; align-items: flex-start; gap: 10px;
      margin-bottom: 20px;
      color: var(--bk-error);
      animation: fadeIn 0.2s ease;
    }
    .booking-alert-text { flex: 1; font-size: 0.875rem; font-weight: 500; }
    .booking-alert-close {
      background: none; border: none; cursor: pointer;
      color: var(--bk-error); padding: 0; display: flex;
      opacity: 0.7;
    }
    .booking-alert-close:hover { opacity: 1; }

    /* ── STEP INDICATOR ── */
    .step-indicator-wrapper {
      display: flex; align-items: center; justify-content: center;
      padding: 0 0 20px;
    }
    .step-indicator-pill {
      display: inline-flex; align-items: center;
      background: var(--bk-card2);
      border: 1px solid var(--bk-border);
      border-radius: 40px;
      padding: 5px 10px;
      gap: 4px;
    }
    .step-item {
      display: flex; align-items: center; gap: 5px;
      padding: 4px 8px;
      border-radius: 30px;
      transition: all 0.25s ease;
    }
    .step-item-active {
      background: var(--bk-accent-light);
    }
    .step-circle {
      width: 22px; height: 22px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 0.65rem; font-weight: 800;
      background: var(--bk-card);
      border: 1.5px solid var(--bk-border);
      color: var(--bk-text-muted);
      transition: all 0.25s ease;
      flex-shrink: 0;
    }
    .step-circle.step-active {
      background: var(--bk-accent);
      border-color: var(--bk-accent);
      color: #fff;
    }
    .step-circle.step-completed {
      background: var(--bk-success);
      border-color: var(--bk-success);
      color: #fff;
    }
    .step-label {
      font-size: 0.7rem; font-weight: 600;
      color: var(--bk-text-muted); white-space: nowrap;
      max-width: 0; overflow: hidden;
      transition: max-width 0.3s ease, opacity 0.3s ease;
      opacity: 0;
    }
    .step-item-active .step-label {
      max-width: 80px; opacity: 1;
      color: var(--bk-accent);
    }
    .step-connector {
      width: 16px; height: 1.5px;
      background: var(--bk-border);
      border-radius: 2px;
      flex-shrink: 0;
      transition: background 0.3s ease;
    }
    .step-connector-done { background: var(--bk-success); }

    /* ── SECTION HEADER ── */
    .section-badge {
      width: 28px; height: 28px; border-radius: 8px;
      background: var(--bk-accent);
      color: #fff; font-size: 0.75rem; font-weight: 800;
      display: inline-flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .section-title {
      font-size: 0.95rem; font-weight: 700;
      color: var(--bk-text-primary); margin: 0;
    }
    .section-subtitle {
      font-size: 0.75rem; color: var(--bk-text-muted);
      margin: 2px 0 0; font-weight: 400;
    }
    .section-divider {
      height: 1px; background: var(--bk-border);
      margin: 20px 0;
    }

    /* ── SKELETON ── */
    .skeleton-box {
      background: linear-gradient(90deg, var(--skeleton-base) 25%, var(--skeleton-shine) 50%, var(--skeleton-base) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    /* ─────────────────────────────────────────────
       STAFF SELECTOR
    ───────────────────────────────────────────── */
    .staff-selector-section { }
    .staff-scroll-container {
      overflow-x: auto;
      overflow-y: visible;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 8px;
      scrollbar-width: thin;
      scrollbar-color: var(--bk-accent-light) transparent;
    }
    .staff-scroll-container::-webkit-scrollbar { height: 4px; }
    .staff-scroll-container::-webkit-scrollbar-track { background: transparent; }
    .staff-scroll-container::-webkit-scrollbar-thumb {
      background: var(--bk-accent-light); border-radius: 4px;
    }
    .staff-cards-row {
      display: flex; gap: 12px;
      padding: 4px 2px 8px;
    }
    .staff-card {
      display: flex; flex-direction: column; align-items: center;
      padding: 16px 12px;
      background: var(--bk-card2);
      border-radius: 18px;
      border: 2px solid transparent;
      cursor: pointer;
      transition: all 0.25s ease;
      min-width: 110px; max-width: 130px;
      position: relative;
      flex-shrink: 0;
      outline: none;
    }
    .staff-card:hover {
      border-color: var(--bk-accent);
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(124,92,252,0.18);
    }
    .staff-card-selected {
      border-color: var(--bk-accent) !important;
      background: var(--bk-accent-light) !important;
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(124,92,252,0.22) !important;
    }
    .staff-avatar-wrapper {
      position: relative; margin-bottom: 10px;
    }
    .staff-avatar-img {
      width: 68px; height: 68px; border-radius: 50%;
      object-fit: cover;
      border: 3px solid var(--bk-accent);
    }
    .staff-avatar-initials {
      width: 68px; height: 68px; border-radius: 50%;
      background: var(--bk-accent);
      color: #fff;
      font-size: 1.1rem; font-weight: 800;
      display: flex; align-items: center; justify-content: center;
      border: 3px solid var(--bk-accent);
    }
    .staff-online-badge {
      position: absolute; bottom: 2px; right: 2px;
      width: 18px; height: 18px; border-radius: 50%;
      background: var(--bk-success);
      color: #fff; border: 2px solid var(--bk-card);
      display: flex; align-items: center; justify-content: center;
    }
    .staff-name {
      font-size: 0.8rem; font-weight: 700;
      color: var(--bk-text-primary);
      text-align: center; margin: 0 0 4px;
      line-height: 1.2;
    }
    .staff-tag {
      font-size: 0.65rem; font-weight: 600;
      padding: 2px 8px;
      border-radius: 20px;
      background: var(--bk-accent-light);
      color: var(--bk-accent);
      margin-bottom: 6px;
      text-align: center;
    }
    .staff-rating {
      display: flex; align-items: center; gap: 3px;
      font-size: 0.72rem; font-weight: 600;
      color: #f59e0b;
    }
    .staff-selected-indicator {
      position: absolute; top: 8px; right: 8px;
      width: 20px; height: 20px; border-radius: 50%;
      background: var(--bk-accent);
      color: #fff;
      display: flex; align-items: center; justify-content: center;
    }

    /* ─────────────────────────────────────────────
       GENDER FILTER
    ───────────────────────────────────────────── */
    .gender-filter-group {
      display: flex; gap: 4px;
      background: var(--bk-card2);
      border: 1px solid var(--bk-border);
      border-radius: 10px;
      padding: 3px;
    }
    .gender-filter-btn {
      background: none; border: none;
      padding: 5px 12px;
      border-radius: 7px;
      font-size: 0.75rem; font-weight: 600;
      color: var(--bk-text-secondary);
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;
    }
    .gender-filter-btn.active {
      background: var(--bk-accent);
      color: #fff;
      box-shadow: 0 2px 8px rgba(124,92,252,0.3);
    }
    .gender-filter-btn:not(.active):hover {
      background: var(--bk-accent-light);
      color: var(--bk-accent);
    }

    /* ─────────────────────────────────────────────
       SERVICE LIST
    ───────────────────────────────────────────── */
    .service-list {
      display: flex; flex-direction: column;
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid var(--bk-border);
    }
    .service-item {
      display: flex; align-items: center;
      padding: 14px 16px; gap: 12px;
      background: var(--bk-card2);
      border-bottom: 1px solid var(--bk-border);
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .service-item:last-child { border-bottom: none; }
    .service-item:hover { background: var(--bk-accent-light); }
    .service-item-selected {
      background: var(--bk-accent) !important;
    }
    .service-item-selected .service-name,
    .service-item-selected .service-description { color: #fff !important; }
    .service-item-selected .service-duration { color: rgba(255,255,255,0.85) !important; border-color: rgba(255,255,255,0.3) !important; }
    .service-item-selected .service-check-box {
      background: rgba(255,255,255,0.25) !important;
      border-color: transparent !important;
      color: #fff !important;
    }
    .service-item-selected .service-popular-badge {
      background: rgba(255,255,255,0.2) !important;
      color: #fff !important;
    }
    .service-item-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
    .service-item-right { flex-shrink: 0; }
    .service-check-box {
      width: 28px; height: 28px; border-radius: 8px;
      border: 2px solid var(--bk-border);
      background: var(--bk-card);
      display: flex; align-items: center; justify-content: center;
      color: var(--bk-accent); flex-shrink: 0;
      transition: all 0.2s;
    }
    .service-details { min-width: 0; }
    .service-name {
      font-size: 0.88rem; font-weight: 700;
      color: var(--bk-text-primary); margin: 0;
    }
    .service-description {
      font-size: 0.75rem; color: var(--bk-text-muted);
      margin: 2px 0 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .service-popular-badge {
      display: inline-flex; align-items: center; gap: 3px;
      font-size: 0.65rem; font-weight: 700;
      padding: 2px 7px; border-radius: 20px;
      background: rgba(245,158,11,0.12);
      color: #f59e0b;
    }
    .service-duration {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 0.75rem; font-weight: 600;
      color: var(--bk-text-secondary);
      padding: 4px 10px; border-radius: 20px;
      border: 1px solid var(--bk-border);
      white-space: nowrap;
    }
    .service-skeleton { display: flex; flex-direction: column; gap: 8px; }
    .service-skeleton-item {
      display: flex; align-items: center; gap: 12px;
      padding: 14px 16px;
      background: var(--bk-card2);
      border-radius: 12px;
    }

    /* ─────────────────────────────────────────────
       OPTIONAL CHIPS
    ───────────────────────────────────────────── */
    .optional-chip {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 14px; border-radius: 20px;
      border: 1.5px solid var(--bk-border);
      background: var(--bk-card2);
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: inherit;
    }
    .optional-chip:hover { border-color: var(--bk-accent); color: var(--bk-accent); }
    .optional-chip-selected {
      background: var(--bk-accent) !important;
      border-color: var(--bk-accent) !important;
      color: #fff !important;
    }
    .optional-chip-check {
      width: 18px; height: 18px; border-radius: 50%;
      background: rgba(255,255,255,0.2);
      display: flex; align-items: center; justify-content: center;
    }
    .optional-chip-name { font-size: 0.8rem; font-weight: 600; color: var(--bk-text-primary); }
    .optional-chip-selected .optional-chip-name { color: #fff; }
    .optional-chip-duration {
      font-size: 0.7rem; font-weight: 500;
      color: var(--bk-text-muted);
      padding: 1px 7px; border-radius: 10px;
      background: rgba(0,0,0,0.08);
    }
    .optional-chip-selected .optional-chip-duration {
      background: rgba(255,255,255,0.2); color: rgba(255,255,255,0.85);
    }
    .optional-selected-info {
      font-size: 0.75rem; color: var(--bk-text-muted); font-weight: 500;
    }

    /* ─────────────────────────────────────────────
       CALENDAR
    ───────────────────────────────────────────── */
    .datetime-grid {
      display: grid; grid-template-columns: 1fr;
      gap: 16px;
    }
    @media (min-width: 640px) {
      .datetime-grid { grid-template-columns: 1fr 1fr; }
    }
    .calendar-wrapper {
      background: var(--bk-card2);
      border-radius: 16px;
      border: 1px solid var(--bk-border);
      overflow: hidden;
      padding: 4px;
    }
    .calendar-wrapper .react-calendar {
      width: 100% !important;
      border: none !important;
      background: transparent !important;
      font-family: 'Plus Jakarta Sans', sans-serif !important;
    }
    .calendar-wrapper .react-calendar__navigation {
      background: transparent;
      margin-bottom: 4px;
    }
    .calendar-wrapper .react-calendar__navigation button {
      color: var(--bk-accent) !important;
      background: none !important;
      font-size: 0.9rem !important;
      font-weight: 700;
      border-radius: 8px;
    }
    .calendar-wrapper .react-calendar__navigation button:hover {
      background: var(--bk-accent-light) !important;
    }
    .calendar-wrapper .react-calendar__month-view__weekdays__weekday {
      color: var(--bk-text-muted) !important;
      font-size: 0.65rem !important;
      font-weight: 700 !important;
      text-transform: uppercase !important;
      letter-spacing: 0.5px !important;
    }
    .calendar-wrapper .react-calendar__month-view__weekdays__weekday abbr {
      text-decoration: none !important;
    }
    .calendar-wrapper .react-calendar__tile {
      background: none !important;
      color: var(--cal-text) !important;
      border-radius: 8px !important;
      font-size: 0.78rem !important;
      font-weight: 600 !important;
      height: 40px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: all 0.15s !important;
    }
    .calendar-wrapper .react-calendar__tile:enabled:hover {
      background: var(--bk-accent-light) !important;
      color: var(--bk-accent) !important;
    }
    .calendar-wrapper .react-calendar__tile--active {
      background: var(--bk-accent) !important;
      color: #fff !important;
      font-weight: 800 !important;
      box-shadow: 0 4px 12px rgba(124,92,252,0.35) !important;
    }
    .calendar-wrapper .react-calendar__tile--now:not(.react-calendar__tile--active) {
      border: 2px solid var(--bk-accent) !important;
      color: var(--bk-accent) !important;
    }
    .calendar-wrapper .react-calendar__tile:disabled {
      opacity: 0.3 !important;
      cursor: not-allowed !important;
    }

    /* ─────────────────────────────────────────────
       TIME SLOTS
    ───────────────────────────────────────────── */
    .timeslots-wrapper {
      background: var(--bk-card2);
      border-radius: 16px;
      border: 1px solid var(--bk-border);
      overflow: hidden;
      display: flex; flex-direction: column;
    }
    .timeslots-header {
      padding: 12px 16px;
      border-bottom: 1px solid var(--bk-border);
      display: flex; align-items: center; justify-content: space-between;
    }
    .timeslots-date {
      font-size: 0.8rem; font-weight: 700; color: var(--bk-text-primary);
    }
    .timeslots-available-count {
      font-size: 0.7rem; font-weight: 600;
      padding: 3px 10px; border-radius: 20px;
      background: rgba(18,184,134,0.1);
      color: var(--bk-success);
    }
    .timeslots-body {
      padding: 12px;
      flex: 1; overflow-y: auto;
      max-height: 300px;
      scrollbar-width: thin;
      scrollbar-color: var(--bk-accent-light) transparent;
    }
    .timeslots-body::-webkit-scrollbar { width: 4px; }
    .timeslots-body::-webkit-scrollbar-thumb { background: var(--bk-accent-light); border-radius: 4px; }
    .timeslots-grid {
      display: flex; flex-wrap: wrap; gap: 8px;
    }
    .timeslots-empty {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; padding: 32px;
      color: var(--bk-text-muted); gap: 8px;
      font-size: 0.85rem; font-weight: 500; text-align: center;
    }
    .timeslots-loading-text {
      font-size: 0.75rem; color: var(--bk-text-muted); margin: 0;
    }
    .time-slot {
      padding: 6px 12px; border-radius: 10px;
      font-size: 0.78rem; font-weight: 700;
      border: 1.5px solid var(--bk-border);
      background: var(--bk-card);
      color: var(--bk-text-primary);
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Plus Jakarta Sans', sans-serif;
      min-width: 68px; text-align: center;
    }
    .time-slot:hover:not(:disabled) {
      border-color: var(--bk-accent);
      color: var(--bk-accent);
      background: var(--bk-accent-light);
    }
    .time-slot-selected {
      background: var(--bk-accent) !important;
      border-color: var(--bk-accent) !important;
      color: #fff !important;
      box-shadow: 0 4px 12px rgba(124,92,252,0.35);
    }
    .time-slot-disabled {
      opacity: 0.3; cursor: not-allowed;
    }
    .time-slot-ghost {
      opacity: 0.4; cursor: not-allowed;
      text-decoration: line-through;
      background: rgba(250,82,82,0.06) !important;
      border-color: rgba(250,82,82,0.2) !important;
      color: var(--bk-error) !important;
    }

    /* ─────────────────────────────────────────────
       SUMMARY CARD
    ───────────────────────────────────────────── */
    .summary-card {
      background: var(--bk-card2);
      border-radius: 18px;
      border: 1px solid var(--bk-border);
      overflow: hidden;
    }
    .summary-staff-header {
      padding: 16px; display: flex; align-items: center; gap: 12px;
      background: linear-gradient(135deg, var(--bk-accent-light), transparent);
    }
    .summary-avatar {
      width: 48px; height: 48px; border-radius: 50%;
      border: 2.5px solid var(--bk-accent);
      overflow: hidden; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      background: var(--bk-accent); color: #fff;
      font-weight: 800; font-size: 1rem;
    }
    .summary-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .summary-staff-name {
      font-size: 0.95rem; font-weight: 800;
      color: var(--bk-text-primary); margin: 0;
    }
    .summary-staff-sub {
      font-size: 0.7rem; color: var(--bk-text-muted); margin: 0;
    }
    .summary-duration-badge {
      display: inline-flex; align-items: center;
      font-size: 0.75rem; font-weight: 700;
      padding: 4px 12px; border-radius: 20px;
      background: var(--bk-accent);
      color: #fff;
    }
    .summary-divider {
      height: 1px; background: var(--bk-border);
    }
    .summary-details { padding: 12px 16px; display: flex; flex-direction: column; gap: 10px; }
    .summary-row {
      display: flex; align-items: flex-start; gap: 12px;
    }
    .summary-row-icon {
      width: 32px; height: 32px; border-radius: 8px;
      background: var(--bk-accent-light);
      display: flex; align-items: center; justify-content: center;
      color: var(--bk-accent); flex-shrink: 0;
    }
    .summary-row-content {
      display: flex; flex-direction: column; gap: 1px;
    }
    .summary-row-label {
      font-size: 0.68rem; font-weight: 600;
      color: var(--bk-text-muted); text-transform: uppercase; letter-spacing: 0.5px;
    }
    .summary-row-value {
      font-size: 0.85rem; font-weight: 700;
      color: var(--bk-text-primary);
    }

    /* ── NOTE TEXTAREA ── */
    .note-wrapper { }
    .note-label {
      display: flex; align-items: center; gap: 6px;
      font-size: 0.8rem; font-weight: 600;
      color: var(--bk-text-secondary); margin-bottom: 8px;
    }
    .note-textarea {
      width: 100%; padding: 12px 14px;
      background: var(--bk-card2);
      border: 1.5px solid var(--bk-border);
      border-radius: 12px;
      color: var(--bk-text-primary);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.85rem; resize: none;
      outline: none;
      transition: border-color 0.2s;
    }
    .note-textarea::placeholder { color: var(--bk-text-muted); }
    .note-textarea:focus { border-color: var(--bk-accent); }

    /* ── CONFIRM BUTTON ── */
    .confirm-btn {
      width: 100%; padding: 16px;
      background: var(--bk-accent);
      border: none; border-radius: 14px;
      color: #fff; font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.95rem; font-weight: 800;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      transition: all 0.2s ease;
      letter-spacing: 0.3px;
    }
    .confirm-btn:hover:not(:disabled) {
      background: var(--bk-accent-hover);
      transform: translateY(-1px);
      box-shadow: 0 8px 24px rgba(124,92,252,0.35);
    }
    .confirm-btn:disabled { opacity: 0.7; cursor: not-allowed; }
    .confirm-btn svg { animation: none; }
    .confirm-btn:disabled svg { animation: spin 1s linear infinite; }

    /* ─────────────────────────────────────────────
       SUCCESS SCREEN
    ───────────────────────────────────────────── */
    .booking-success-screen {
      display: flex; flex-direction: column;
      align-items: center; padding: 40px 24px;
      text-align: center;
    }
    .success-animation-wrapper {
      position: relative; margin-bottom: 24px;
      display: flex; align-items: center; justify-content: center;
    }
    .success-circle {
      width: 80px; height: 80px; border-radius: 50%;
      background: var(--bk-success);
      display: flex; align-items: center; justify-content: center;
      color: #fff;
      animation: successPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
      position: relative; z-index: 1;
    }
    .success-ripple {
      position: absolute;
      width: 80px; height: 80px; border-radius: 50%;
      background: rgba(18,184,134,0.3);
      animation: ripple 1.5s ease-out infinite;
    }
    .success-ripple-2 { animation-delay: 0.75s; }
    .success-title {
      font-size: 1.5rem; font-weight: 800;
      color: var(--bk-text-primary); margin: 0 0 8px;
    }
    .success-subtitle {
      font-size: 0.875rem; color: var(--bk-text-muted);
      margin: 0 0 28px;
    }
    .success-details-card {
      width: 100%; max-width: 360px;
      background: var(--bk-card2);
      border: 1px solid var(--bk-border);
      border-radius: 18px;
      overflow: hidden; margin-bottom: 24px;
    }
    .success-detail-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 12px 18px;
      border-bottom: 1px solid var(--bk-border);
    }
    .success-detail-row:last-child { border-bottom: none; }
    .success-detail-label {
      font-size: 0.78rem; font-weight: 600;
      color: var(--bk-text-muted);
    }
    .success-detail-value {
      font-size: 0.85rem; font-weight: 700;
      color: var(--bk-text-primary);
    }
    .success-detail-value.highlight {
      color: var(--bk-accent); font-size: 1rem;
    }
    .success-new-btn {
      padding: 13px 36px;
      background: var(--bk-accent-light);
      border: 1.5px solid var(--bk-accent);
      border-radius: 12px;
      color: var(--bk-accent);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.875rem; font-weight: 700;
      cursor: pointer; transition: all 0.2s ease;
    }
    .success-new-btn:hover {
      background: var(--bk-accent); color: #fff;
    }

    /* ─────────────────────────────────────────────
       FULL PAGE LOADER
    ───────────────────────────────────────────── */
    .full-loader {
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      min-height: 60vh; gap: 16px;
    }
    .full-loader-spinner {
      width: 48px; height: 48px; border-radius: 50%;
      border: 3px solid var(--bk-accent-light);
      border-top-color: var(--bk-accent);
      animation: spin 0.8s linear infinite;
    }
    .full-loader-text {
      font-size: 0.875rem; font-weight: 600;
      color: var(--bk-text-muted);
    }

    /* ── EMPTY STATE ── */
    .empty-state {
      padding: 24px; text-align: center;
      background: var(--bk-card2); border-radius: 12px;
      border: 1px dashed var(--bk-border);
      color: var(--bk-text-muted);
      font-size: 0.85rem; font-weight: 500;
    }
  `}</style>
)

// ─── STEP INDICATOR ──────────────────────────────────────────────────────────
const STEPS = [
    { number: 1, label: "Uzman" },
    { number: 2, label: "Hizmet" },
    { number: 3, label: "Tarih & Saat" },
    { number: 4, label: "Onay" },
]

const StepIndicator = ({ currentStep }) => (
    <div className="step-indicator-wrapper">
        <div className="step-indicator-pill">
            {STEPS.map((step, idx) => {
                const isCompleted = currentStep > step.number
                const isActive = currentStep === step.number
                return (
                    <React.Fragment key={step.number}>
                        <div className={`step-item ${isActive ? "step-item-active" : ""}`}>
                            <div className={`step-circle ${isCompleted ? "step-completed" : ""} ${isActive ? "step-active" : ""}`}>
                                {isCompleted ? <CheckIcon size={10} /> : <span>{step.number}</span>}
                            </div>
                            <span className="step-label">{step.label}</span>
                        </div>
                        {idx < STEPS.length - 1 && (
                            <div className={`step-connector ${isCompleted ? "step-connector-done" : ""}`} />
                        )}
                    </React.Fragment>
                )
            })}
        </div>
    </div>
)

// ─── STAFF CARD ───────────────────────────────────────────────────────────────
const StaffCard = ({ staff, isSelected, onClick }) => (
    <div
        className={`staff-card ${isSelected ? "staff-card-selected" : ""}`}
        onClick={() => onClick(staff)}
        role="button" tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onClick(staff)}
        aria-pressed={isSelected}
    >
        <div className="staff-avatar-wrapper">
            {staff.picture ? (
                <img src={staff.picture} alt={staff.name} className="staff-avatar-img" />
            ) : (
                <div className="staff-avatar-initials">{getInitials(staff.name)}</div>
            )}
            <div className="staff-online-badge">
                <CheckIcon size={8} />
            </div>
        </div>
        <p className="staff-name">{staff.name}</p>
        {staff.tag && <span className="staff-tag">{staff.tag}</span>}
        <div className="staff-rating">
            <StarIcon size={11} />
            <span>{staff.rating}</span>
        </div>
        {isSelected && (
            <div className="staff-selected-indicator">
                <CheckIcon size={10} />
            </div>
        )}
    </div>
)

// ─── SERVICE ITEM ─────────────────────────────────────────────────────────────
const ServiceItem = ({ service, isSelected, onClick }) => (
    <div
        className={`service-item ${isSelected ? "service-item-selected" : ""}`}
        onClick={() => onClick(service)}
        role="button" tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onClick(service)}
        aria-pressed={isSelected}
    >
        <div className="service-item-left">
            <div className="service-check-box">
                {isSelected && <CheckIcon size={13} />}
            </div>
            <div className="service-details">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="service-name">{service.name}</span>
                    {service.is_favorite && (
                        <span className="service-popular-badge">
              <StarIcon size={10} /> Popüler
            </span>
                    )}
                </div>
                {service.description && (
                    <p className="service-description">{service.description}</p>
                )}
            </div>
        </div>
        <div className="service-item-right">
      <span className="service-duration">
        <ClockIcon size={12} />
          {service.duration} dk
      </span>
        </div>
    </div>
)

// ─── OPTIONAL CHIP ────────────────────────────────────────────────────────────
const OptionalChip = ({ service, isSelected, onClick }) => (
    <button
        className={`optional-chip ${isSelected ? "optional-chip-selected" : ""}`}
        onClick={() => onClick(service)}
    >
        {isSelected && <span className="optional-chip-check"><CheckIcon size={10} /></span>}
        <span className="optional-chip-name">{service.name}</span>
        <span className="optional-chip-duration">{service.duration} dk</span>
    </button>
)

// ─── TIME SLOT ────────────────────────────────────────────────────────────────
const TimeSlot = ({ slot, isSelected, onSelect }) => {
    const isDisabled = !slot.isAvailable
    let cls = "time-slot"
    if (isSelected) cls += " time-slot-selected"
    else if (slot.isGhost) cls += " time-slot-ghost"
    else if (isDisabled) cls += " time-slot-disabled"

    return (
        <button
            className={cls}
            onClick={() => !isDisabled && onSelect(slot.time)}
            disabled={isDisabled}
            title={slot.isGhost ? "Önceki randevular nedeniyle kaydırıldı" : ""}
        >
            {slot.time}
        </button>
    )
}

// ─── SUMMARY ROW ──────────────────────────────────────────────────────────────
const SummaryRow = ({ icon, label, value }) => (
    <div className="summary-row">
        <div className="summary-row-icon">{icon}</div>
        <div className="summary-row-content">
            <span className="summary-row-label">{label}</span>
            <span className="summary-row-value">{value}</span>
        </div>
    </div>
)

// ─── SUCCESS SCREEN ───────────────────────────────────────────────────────────
const BookingSuccess = ({
  selectedStaff,
  cart,
  selectedDate,
  selectedTime,
  onReset,
}) => (
  <div className="booking-success-screen">
    <div className="success-animation-wrapper">
      <div className="success-circle">
        <CheckIcon size={36} />
      </div>
      <div className="success-ripple" />
      <div className="success-ripple success-ripple-2" />
    </div>
    <h2 className="success-title">Randevu Onaylandı!</h2>
    <p className="success-subtitle">
      Randevunuz başarıyla oluşturuldu. Görüşmek üzere!
    </p>
    <div className="success-details-card">
      <div className="success-detail-row">
        <span className="success-detail-label">Uzman</span>
        <span className="success-detail-value">{selectedStaff?.name}</span>
      </div>
      <div className="success-detail-row">
        <span className="success-detail-label">Hizmetler</span>
        {/* Sepetteki tüm hizmetleri araya virgül koyarak yazdırıyoruz */}
        <span className="success-detail-value">
          {cart.map((s) => s.name).join(", ")}
        </span>
      </div>
      <div className="success-detail-row">
        <span className="success-detail-label">Tarih</span>
        <span className="success-detail-value">{formatDate(selectedDate)}</span>
      </div>
      <div className="success-detail-row">
        <span className="success-detail-label">Saat</span>
        <span className="success-detail-value highlight">{selectedTime}</span>
      </div>
    </div>
    <button className="success-new-btn" onClick={onReset}>
      Yeni Randevu Al
    </button>
  </div>
);
// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const CustomerBooking = () => {
  const [staffList, setStaffList] = useState([]);
  const [mainServices, setMainServices] = useState([]);
  const [optionalServices, setOptionalServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [cart, setCart] = useState([]); // YENİ: Artık her şeyi tek sepette tutuyoruz

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [isTimeLoading, setIsTimeLoading] = useState(false);
  const [isServicesLoading, setIsServicesLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [error, setError] = useState(null);
  const [genderFilter, setGenderFilter] = useState("all");

  const { user, logout } = useAuth();

  // Auto-scroll refs
  const serviceRef = useRef(null);
  const datetimeRef = useRef(null);
  const summaryRef = useRef(null);

  const maxDateLimit = new Date();
  maxDateLimit.setDate(maxDateLimit.getDate() + 60);

  const handleAuthError = () => logout();

  // ── Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!user) throw new Error("Kullanıcı oturumu bulunamadı.");

        const [staffResponse, servicesResponse] = await Promise.all([
          usersAPI.getStaff(),
          servicesAPI.getAll(),
        ]);

        if (!staffResponse?.staff)
          throw new Error("Personel verisi alınamadı.");
        if (!servicesResponse?.services)
          throw new Error("Hizmet verisi alınamadı.");

        const transformedStaff = staffResponse.staff.map((staff) => ({
          id: staff.id,
          name: `${staff.name} ${staff.surname}`,
          picture: staff.profile_picture,
          rating: staff.average_rating
            ? staff.average_rating.toFixed(1)
            : "5.0",
          schedule: transformWorkDaysToSchedule(staff.workDays || []),
          tag: staff.tag || "Profesyonel",
        }));

        const allServices = servicesResponse.services;
        setStaffList(transformedStaff);
        setMainServices(allServices); // Tümü ana havuza eklendi
        setOptionalServices([]);

      } catch (err) {
        if (
          err.message.includes("Token Süresi Dolmuştur") ||
          err.message.includes("Token Bulunamadi") ||
          err.message.includes("401") ||
          err.message.includes("Unauthorized")
        ) {
          handleAuthError();
          return;
        }
        let msg = "Veriler yüklenirken bir hata oluştu. ";
        if (err.message.includes("Failed to fetch"))
          msg += "Sunucuya bağlanılamıyor.";
        else if (err.response?.data?.message) msg += err.response.data.message;
        else msg += err.message || "Lütfen tekrar deneyin.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [user]);

  // ── Fetch time slots
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!selectedStaff || cart.length === 0) return;
      try {
        setIsTimeLoading(true);
        setTimeSlots([]);
        setError(null);
        setSelectedTime(null);

        const selectedDateStr = formatDate(selectedDate);
        const response = await workScheduleAPI.getAvailableSlots(
          selectedStaff.id,
          selectedDateStr
        );
        const slotsForDate = response.available_slots?.[selectedDateStr] || [];

        if (slotsForDate.length === 0) {
          setTimeSlots([]);
          setError(`${selectedDateStr} tarihi için randevu doludur.`);
          return;
        }

        const totalDuration = cart.reduce(
          (sum, s) => sum + parseInt(s.duration || 0),
          0
        );

        const now = new Date();
        const isToday = selectedDate.toDateString() === now.toDateString();
        const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();

        const sortedSlots = slotsForDate.sort(
          (a, b) =>
            timeToMinutes(a.start_time || a.time) -
            timeToMinutes(b.start_time || b.time)
        );

        let finalDisplayList = [];

        sortedSlots.forEach((slot, index) => {
          const timeStr = slot.start_time || slot.time;
          if (!timeStr) return;

          const slotStartMinutes = timeToMinutes(timeStr);
          const slotEndMinutes = slot.end_time
            ? timeToMinutes(slot.end_time)
            : slotStartMinutes + 15;

          // Ghost slot
          if (slot.old_time && slot.old_time !== timeStr) {
            const isAlreadyThere = finalDisplayList.some(
              (f) => f.time === slot.old_time.substring(0, 5)
            );
            if (!isAlreadyThere) {
              finalDisplayList.push({
                time: slot.old_time.substring(0, 5),
                isAvailable: false,
                isGhost: true,
                workHourId: `ghost-${slot.id}`,
              });
            }
          }

          let isPastTime = isToday && slotStartMinutes <= currentTotalMinutes;
          let isSlotAvailable =
            !isPastTime &&
            (slot.is_available === true ||
              slot.is_available === 1 ||
              slot.is_available === "1");

          // Chain check
          if (isSlotAvailable) {
            const requiredEndTime = slotStartMinutes + totalDuration;
            let currentChainTime = slotStartMinutes;
            let chainBroken = false;

            for (let i = index; i < sortedSlots.length; i++) {
              const checkSlot = sortedSlots[i];
              const checkStart = timeToMinutes(
                checkSlot.start_time || checkSlot.time
              );
              const checkEnd = checkSlot.end_time
                ? timeToMinutes(checkSlot.end_time)
                : checkStart + 15;

              if (checkStart > currentChainTime + 1) {
                chainBroken = true;
                break;
              }

              const isCheckAvailable =
                checkSlot.is_available === true ||
                checkSlot.is_available === 1 ||
                checkSlot.is_available === "1";

              if (!isCheckAvailable) {
                chainBroken = true;
                break;
              }

              currentChainTime = checkEnd;
              if (currentChainTime >= requiredEndTime) break;
            }

            if (chainBroken || currentChainTime < requiredEndTime) {
              isSlotAvailable = false;
            }
          }

          finalDisplayList.push({
            time: timeStr.substring(0, 5),
            isAvailable: isSlotAvailable,
            isPastTime,
            isGhost: false,
            workHourId: slot.id,
            rawSlot: slot,
          });
        });

        finalDisplayList.sort(
          (a, b) => timeToMinutes(a.time) - timeToMinutes(b.time)
        );
        setTimeSlots(finalDisplayList);

        if (finalDisplayList.filter((s) => s.isAvailable).length === 0) {
          setError("Seçilen işlem süresi için uygun boşluk bulunamadı.");
        }
      } catch (err) {
        console.error("Slot Error:", err);
        setError("Saatler yüklenirken hata oluştu.");
      } finally {
        setIsTimeLoading(false);
      }
    };
    fetchTimeSlots();
  }, [selectedStaff, cart, selectedDate]);

  // ── Handlers
  const handleSelectStaff = async (staff) => {
    setSelectedStaff(staff);
    setCart([]);
    setSelectedTime(null);
    setTimeSlots([]);
    setError(null);
    setGenderFilter("all");
    setIsServicesLoading(true);

    // Auto-scroll to services section
    setTimeout(
      () =>
        serviceRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      100
    );

    try {
      const response = await api.get(`/services?staff_id=${staff.id}`);
      if (response.success && response.services) {
        const staffServices = response.services;

        setMainServices(staffServices); // Tüm hizmetler tek havuzda
        setOptionalServices([]);
      }
    } catch (err) {
      console.error("Personel hizmetleri yüklenemedi:", err);
      setError("Bu personelin hizmet bilgileri alınamadı.");
    } finally {
      setIsServicesLoading(false);
    }
  };

  const handleToggleService = (service) => {
    setCart((prev) => {
      const exists = prev.some((s) => s.id === service.id);
      if (exists) {
        // Varsa sepetten çıkar
        const newCart = prev.filter((s) => s.id !== service.id);
        if (newCart.length === 0) setSelectedTime(null);
        return newCart;
      } else {
        // Yoksa sepete ekle (Maksimum 3 limitli)
        if (prev.length >= 3) {
          setError("En fazla 3 hizmet seçebilirsiniz.");
          setTimeout(() => setError(null), 3000);
          return prev;
        }
        if (prev.length === 0) {
          setTimeout(
            () =>
              datetimeRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              }),
            100
          );
        }
        return [...prev, service];
      }
    });
  };

  const handleConfirmBooking = async () => {
    try {
      setError(null);
      setIsConfirming(true);
      if (!user?.id) {
        setError("Oturum süreniz dolmuş.");
        return;
      }

      const appointmentData = {
        customer_id: user.id,
        staff_id: selectedStaff.id,
        // İLLÜZYON BURADA: Frontend sepet sandı, backend 3 ayrı hizmet sanıyor!
        service_id: cart[0]?.id,
        optional_service_id_1: cart[1]?.id || null,
        optional_service_id_2: cart[2]?.id || null,
        appointment_date: formatDate(selectedDate),
        appointment_time: selectedTime,
        note: note || null,
        status: "approved",
      };

      await appointmentsAPI.create(appointmentData);
      setBookingStatus("success");
      setTimeout(() => handleReset(), 5000);
    } catch (err) {
      setError(err.response?.data?.message || "Randevu oluşturulamadı.");
    } finally {
      setIsConfirming(false);
    }
  };

  const handleReset = () => {
    setSelectedStaff(null);
    setCart([]);
    setSelectedDate(new Date());
    setSelectedTime(null);
    setNote("");
    setBookingStatus(null);
    setError(null);
    setTimeSlots([]);
  };

  const isDayDisabled = ({ date, view }) => {
    if (view === "month") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      if (checkDate < today) return true;
      if (selectedStaff) {
        if (
          !selectedStaff.schedule ||
          Object.keys(selectedStaff.schedule).length === 0
        )
          return false;
        const dayName = DAY_NAMES[date.getDay()];
        return !Object.prototype.hasOwnProperty.call(
          selectedStaff.schedule,
          dayName
        );
      }
    }
    return false;
  };

  // ── Derived state
  const currentStep = selectedTime
    ? 4
    : cart.length > 0
    ? 3
    : selectedStaff
    ? 2
    : 1;



  const stepNumber_datetime = 3;
  const stepNumber_summary = 4;

  const filteredServices = mainServices
    .filter((service) => {
      if (genderFilter === "all") return true;
      if (genderFilter === "male")
        return (
          service.target_gender === "male" ||
          service.target_gender === "unisex" ||
          !service.target_gender
        );
      if (genderFilter === "female")
        return (
          service.target_gender === "female" ||
          service.target_gender === "unisex" ||
          !service.target_gender
        );
      return true;
    })
    .sort((a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0));

  const totalDuration = cart.reduce(
    (sum, s) => sum + parseInt(s.duration || 0),
    0
  );

  const availableCount = timeSlots.filter((s) => s.isAvailable).length;

  // ── Loading state
  if (loading) {
    return (
      <div className="booking-page-wrapper">
        <GlobalStyles />
        <div className="full-loader">
          <div className="full-loader-spinner" />
          <p className="full-loader-text">Uzman ve hizmetler yükleniyor...</p>
        </div>
      </div>
    );
  }

  // ── Success state
  if (bookingStatus === "success") {
    return (
      <div className="booking-page-wrapper">
        <GlobalStyles />
        <div className="booking-main-card">
          <BookingSuccess
            selectedStaff={selectedStaff}
            cart={cart} /* YENİ: Sepeti gönderdik */
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onReset={handleReset}
          />
        </div>
      </div>
    );
  }

  // ── Main render
  return (
    <div className="booking-page-wrapper">
      <GlobalStyles />

      {/* Header */}
      <div className="booking-header-card">
        <div className="booking-header-icon">
          <ScissorsIcon />
        </div>
        <div>
          <h1 className="booking-header-title">Randevu Planla</h1>
          <p className="booking-header-sub">
            Tarzını yansıtmak için en iyi zaman
          </p>
        </div>
      </div>

      {/* Main Card */}
      <div className="booking-main-card">
        <div className="booking-main-body">
          {/* Step Progress Pill */}
          <StepIndicator currentStep={currentStep} />

          {/* Alert */}
          {error && (
            <div className="booking-alert">
              <AlertIcon />
              <span className="booking-alert-text">{error}</span>
              <button
                className="booking-alert-close"
                onClick={() => setError(null)}
              >
                <XIcon />
              </button>
            </div>
          )}

          {/* ── STEP 1: STAFF ── */}
          <div className="staff-selector-section">
            <div className="d-flex align-items-center mb-3 gap-2">
              <span className="section-badge">1</span>
              <div>
                <h6 className="section-title">Uzman Seçin</h6>
                <p className="section-subtitle">
                  Hizmet almak istediğiniz uzmanı seçin
                </p>
              </div>
            </div>
            <div className="staff-scroll-container">
              <div className="staff-cards-row">
                {staffList
                  .filter(
                    (staff) => !staff.name.toLowerCase().includes("izinli")
                  )
                  .map((staff) => (
                    <StaffCard
                      key={staff.id}
                      staff={staff}
                      isSelected={selectedStaff?.id === staff.id}
                      onClick={handleSelectStaff}
                    />
                  ))}
              </div>
            </div>
          </div>

          {/* ── STEP 2: SERVICES ── */}
          {selectedStaff && (
            <div className="slide-up" ref={serviceRef}>
              <div className="section-divider" />
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="section-badge">2</span>
                  <div>
                    <h6 className="section-title">Hizmet Seçin</h6>
                  </div>
                </div>
                {/* Gender Filter */}
                <div className="gender-filter-group">
                  {[
                    { value: "all", label: "Tümü" },
                    { value: "female", label: "Kadın" },
                    { value: "male", label: "Erkek" },
                  ].map((f) => (
                    <button
                      key={f.value}
                      className={`gender-filter-btn ${
                        genderFilter === f.value ? "active" : ""
                      }`}
                      onClick={() => setGenderFilter(f.value)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {isServicesLoading ? (
                <div className="service-skeleton">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="service-skeleton-item">
                      <div
                        className="skeleton-box"
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          className="skeleton-box mb-2"
                          style={{
                            width: "60%",
                            height: "14px",
                            borderRadius: "6px",
                          }}
                        />
                        <div
                          className="skeleton-box"
                          style={{
                            width: "40%",
                            height: "11px",
                            borderRadius: "6px",
                          }}
                        />
                      </div>
                      <div
                        className="skeleton-box"
                        style={{
                          width: "60px",
                          height: "26px",
                          borderRadius: "20px",
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : filteredServices.length === 0 ? (
                <div className="empty-state">
                  Seçilen kategoriye uygun hizmet bulunamadı.
                </div>
              ) : (
                <div className="service-list">
                  {filteredServices.map((service) => (
                    <ServiceItem
                      key={service.id}
                      service={service}
                      isSelected={cart.some((s) => s.id === service.id)}
                      onClick={handleToggleService}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: OPTIONAL SERVICES ──
            {selectedService && optionalServices.length > 0 && (
              <div className="slide-up">
                <div className="section-divider" />
                <div className="d-flex align-items-center mb-2 gap-2">
                  <span className="section-badge">{stepNumber_extras}</span>
                  <div>
                    <h6 className="section-title">Ekstra Hizmetler</h6>
                    <p className="section-subtitle">
                      İsteğe bağlı, en fazla 2 ekstra seçebilirsiniz
                    </p>
                  </div>
                </div>
                <div className="d-flex mt-3 flex-wrap gap-2">
                  {optionalServices.map((service) => {
                    const isSel = selectedOptionalServices.some(
                      (s) => s.id === service.id
                    );
                    return (
                      <OptionalChip
                        key={service.id}
                        service={service}
                        isSelected={isSel}
                        onClick={handleToggleOptionalService}
                      />
                    );
                  })}
                </div>
                {selectedOptionalServices.length > 0 && (
                  <p className="optional-selected-info mt-2">
                    {selectedOptionalServices.length}/2 ekstra seçildi
                  </p>
                )}
              </div>
            )}
        */}

          {/* ── STEP 4: DATE & TIME ── */}
          {cart.length > 0 && (
            <div className="slide-up" ref={datetimeRef}>
              <div className="section-divider" />
              <div className="d-flex align-items-center mb-3 gap-2">
                <span className="section-badge">{stepNumber_datetime}</span>
                <div>
                  <h6 className="section-title">Tarih & Saat Seçin</h6>
                  <p className="section-subtitle">
                    Uygun bir gün ve saat belirleyin
                  </p>
                </div>
              </div>

              <div className="datetime-grid">
                {/* Calendar */}
                <div className="calendar-wrapper">
                  <Calendar
                    onChange={setSelectedDate}
                    value={selectedDate}
                    tileDisabled={isDayDisabled}
                    maxDate={maxDateLimit}
                    locale="tr-TR"
                  />
                </div>

                {/* Time Slots */}
                <div className="timeslots-wrapper">
                  <div className="timeslots-header">
                    <div className="d-flex align-items-center gap-2">
                      <ClockIcon size={15} />
                      <span className="timeslots-date">
                        {selectedDate.toLocaleDateString("tr-TR", {
                          day: "numeric",
                          month: "long",
                          weekday: "long",
                        })}
                      </span>
                    </div>
                    {!isTimeLoading && availableCount > 0 && (
                      <span className="timeslots-available-count">
                        {availableCount} boş
                      </span>
                    )}
                  </div>
                  <div className="timeslots-body">
                    {isTimeLoading ? (
                      <div className="py-3 text-center">
                        <div className="d-flex flex-wrap gap-2">
                          {[...Array(10)].map((_, i) => (
                            <div
                              key={i}
                              className="skeleton-box"
                              style={{
                                width: "68px",
                                height: "34px",
                                borderRadius: "8px",
                              }}
                            />
                          ))}
                        </div>
                        <p className="timeslots-loading-text mt-3">
                          Saatler kontrol ediliyor...
                        </p>
                      </div>
                    ) : timeSlots.length === 0 ? (
                      <div className="timeslots-empty">
                        <ClockIcon size={28} />
                        <p>Bu gün için uygun saat bulunamadı.</p>
                      </div>
                    ) : (
                      <div className="timeslots-grid">
                        {timeSlots.map((slot) => (
                          <TimeSlot
                            key={`${slot.time}-${slot.workHourId}`}
                            slot={slot}
                            isSelected={selectedTime === slot.time}
                            onSelect={setSelectedTime}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 5: SUMMARY & CONFIRM ── */}
          {selectedTime && (
            <div className="slide-up" ref={summaryRef}>
              <div className="section-divider" />
              <div className="d-flex align-items-center mb-3 gap-2">
                <span className="section-badge">{stepNumber_summary}</span>
                <div>
                  <h6 className="section-title">Randevu Özeti</h6>
                  <p className="section-subtitle">
                    Bilgilerinizi kontrol edip onaylayın
                  </p>
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-staff-header">
                  <div className="summary-avatar">
                    {selectedStaff?.picture ? (
                      <img
                        src={selectedStaff.picture}
                        alt={selectedStaff.name}
                      />
                    ) : (
                      <span>{getInitials(selectedStaff?.name)}</span>
                    )}
                  </div>
                  <div>
                    <p className="summary-staff-name">{selectedStaff?.name}</p>
                    <p className="summary-staff-sub">Uzman</p>
                  </div>
                  <div className="ms-auto">
                    <span className="summary-duration-badge">
                      {totalDuration} dk
                    </span>
                  </div>
                </div>
                <div className="summary-divider" />
                <div className="summary-details">
                  <SummaryRow
                    icon={<ScissorsIcon />}
                    label="İlk Hizmet"
                    value={cart[0]?.name}
                  />
                  {cart.length > 1 && (
                    <SummaryRow
                      icon={<StarIcon size={16} />}
                      label="Diğer Hizmetler"
                      value={cart
                        .slice(1)
                        .map((s) => s.name)
                        .join(", ")}
                    />
                  )}
                  <SummaryRow
                    icon={<CalendarIcon size={16} />}
                    label="Tarih"
                    value={formatDate(selectedDate)}
                  />
                  <SummaryRow
                    icon={<ClockIcon size={16} />}
                    label="Saat"
                    value={selectedTime}
                  />
                </div>
              </div>

              {/* Note */}
              <div className="note-wrapper mt-3">
                <label className="note-label">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  Not ekle (isteğe bağlı)
                </label>
                <textarea
                  className="note-textarea"
                  placeholder="Özel bir isteğiniz var mı?"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              {/* Confirm */}
              <button
                className="confirm-btn mt-3"
                onClick={handleConfirmBooking}
                disabled={isConfirming}
              >
                {isConfirming ? (
                  <>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ animation: "spin 1s linear infinite" }}
                    >
                      <path
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        strokeOpacity="0.25"
                      />
                      <path d="M21 12a9 9 0 00-9-9" />
                    </svg>
                    <span>Randevu Oluşturuluyor...</span>
                  </>
                ) : (
                  <>
                    <CheckIcon size={18} />
                    <span>Randevuyu Onayla</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CustomerBooking